import { useEffect } from "react";

const EDIT_TEXTAREA_SELECTOR =
  ".sendbird-message-input__edit .sendbird-message-input--textarea";
const MIN_HEIGHT_PX = 58;
const MAX_HEIGHT_PX = 92;
const HORIZONTAL_PADDING_PX = 16;

function centerEditTextarea(textarea: HTMLElement) {
  const styles = window.getComputedStyle(textarea);
  const lineHeight = Number.parseFloat(styles.lineHeight) || 20;
  const borderHeight =
    (Number.parseFloat(styles.borderTopWidth) || 0) +
    (Number.parseFloat(styles.borderBottomWidth) || 0);
  const text = textarea.innerText.replace(/\n$/, "");
  const lineCount = text ? text.split("\n").length : 1;
  const contentHeight = lineCount * lineHeight;
  const boxHeight = Math.min(
    MAX_HEIGHT_PX,
    Math.max(MIN_HEIGHT_PX, Math.ceil(contentHeight + borderHeight + 8))
  );
  const verticalPadding = Math.max(
    0,
    (boxHeight - borderHeight - contentHeight) / 2
  );

  textarea.style.boxSizing = "border-box";
  textarea.style.height = `${boxHeight}px`;
  textarea.style.minHeight = `${MIN_HEIGHT_PX}px`;
  textarea.style.maxHeight = `${MAX_HEIGHT_PX}px`;
  textarea.style.paddingTop = `${verticalPadding}px`;
  textarea.style.paddingBottom = `${verticalPadding}px`;
  textarea.style.paddingLeft = `${HORIZONTAL_PADDING_PX}px`;
  textarea.style.paddingRight = `${HORIZONTAL_PADDING_PX}px`;
  textarea.style.lineHeight = styles.lineHeight;
  textarea.style.overflowY = "auto";
}

function scheduleCenterEditTextarea(textarea: HTMLElement) {
  centerEditTextarea(textarea);
  requestAnimationFrame(() => {
    centerEditTextarea(textarea);
    window.setTimeout(() => centerEditTextarea(textarea), 0);
  });
}

function syncEditTextareas(root: ParentNode) {
  root.querySelectorAll<HTMLElement>(EDIT_TEXTAREA_SELECTOR).forEach(scheduleCenterEditTextarea);
}

function isEditTextarea(target: EventTarget | null): target is HTMLElement {
  return (
    target instanceof HTMLElement &&
    target.matches(EDIT_TEXTAREA_SELECTOR)
  );
}

export function useMessageEditTextCenter() {
  useEffect(() => {
    const root = document.querySelector(".messages-page");
    if (!root) {
      return;
    }

    const handleInput = (event: Event) => {
      if (isEditTextarea(event.target)) {
        centerEditTextarea(event.target);
      }
    };

    const observer = new MutationObserver(() => syncEditTextareas(root));
    observer.observe(root, { childList: true, subtree: true });
    root.addEventListener("input", handleInput);
    syncEditTextareas(root);

    return () => {
      observer.disconnect();
      root.removeEventListener("input", handleInput);
    };
  }, []);
}
